import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { ALL_COLUMNS, ALWAYS_VISIBLE } from "../../../../util/helper";
import { isRemovedJobDataKey, sanitizeRemovedJobKeys } from "../../../../util/removedJobGuards";

const getDefaultVisibleColumns = () =>
  sanitizeRemovedJobKeys(
    ALL_COLUMNS
      .filter((column) => column.defaultVisible !== false)
      .map((column) => column.key)
  );

const getInitialVisibleColumns = (persistedColumns) => {
  const cleaned = sanitizeRemovedJobKeys(persistedColumns);
  return cleaned.length ? cleaned : getDefaultVisibleColumns();
};

const haveSameKeys = (left = [], right = []) => {
  const leftKeys = sanitizeRemovedJobKeys(left);
  const rightKeys = sanitizeRemovedJobKeys(right);
  if (leftKeys.length !== rightKeys.length) return false;

  const rightSet = new Set(rightKeys);
  return leftKeys.every((key) => rightSet.has(key));
};

export const useColumnManager = (dynamicColumnGroups) => {
  const {
    dynamicGlideColumns,
    dynamicPhaseColumns,
    dynamicAdmeColumns,
    dynamicMolProps,
  } = dynamicColumnGroups;

  const persistedVisibleColumns = useSelector((state) => state.batchResult.visibleColumns);
  const [colsPopoverVis, setColsPopoverVis] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(() => getInitialVisibleColumns(persistedVisibleColumns));
  const userTouchedRef = useRef(false);

  const setVisibleColumnsIfChanged = useCallback((nextColumns) => {
    setVisibleColumns((prev) => {
      const resolved = typeof nextColumns === "function" ? nextColumns(prev) : nextColumns;
      const cleaned = sanitizeRemovedJobKeys(resolved);
      return haveSameKeys(prev, cleaned) ? prev : cleaned;
    });
  }, []);

  const setUserVisibleColumns = useCallback((nextColumns) => {
    userTouchedRef.current = true;
    setVisibleColumnsIfChanged(nextColumns);
  }, [setVisibleColumnsIfChanged]);

  useEffect(() => {
    const cleanedPersisted = sanitizeRemovedJobKeys(persistedVisibleColumns);
    setVisibleColumnsIfChanged((prev) => {
      const next = cleanedPersisted.length
        ? cleanedPersisted
        : (sanitizeRemovedJobKeys(prev).length ? sanitizeRemovedJobKeys(prev) : getDefaultVisibleColumns());

      return next;
    });
  }, [persistedVisibleColumns, setVisibleColumnsIfChanged]);

  const [expandedGroups, setExpandedGroups] = useState({
    schrodinger: true,
    glide: true,
    phase: true,
    adme: true,
    molprops: true,
  });

  const allPossibleDynamicKeys = useMemo(
    () => sanitizeRemovedJobKeys([
      ...dynamicGlideColumns.map((c) => c.key),
      ...dynamicPhaseColumns.map((c) => c.key),
      ...dynamicAdmeColumns.map((c) => c.key),
      ...dynamicMolProps.map((c) => c.key),
    ]),
    [dynamicGlideColumns, dynamicPhaseColumns, dynamicAdmeColumns, dynamicMolProps]
  );

  useEffect(() => {
    const defaultStaticKeys = getDefaultVisibleColumns();
    const desiredAll = Array.from(new Set([...defaultStaticKeys, ...allPossibleDynamicKeys]));

    if (!userTouchedRef.current) {
      setVisibleColumnsIfChanged(desiredAll);
      return;
    }

    setVisibleColumnsIfChanged((prev) => {
      const cleanedPrev = sanitizeRemovedJobKeys(prev);
      const prevSet = new Set(cleanedPrev);
      const newOnes = allPossibleDynamicKeys.filter((key) => !prevSet.has(key));
      return newOnes.length ? [...cleanedPrev, ...newOnes] : cleanedPrev;
    });
  }, [allPossibleDynamicKeys, setVisibleColumnsIfChanged]);

  const handleParentChange = (e, groupKey, dynamicSubColumns) => {
    if (isRemovedJobDataKey(groupKey)) return;
    userTouchedRef.current = true;
    const subKeys = sanitizeRemovedJobKeys(dynamicSubColumns.map((c) => c.key));

    if (e.target.checked) {
      setUserVisibleColumns((prev) => [...prev, groupKey, ...subKeys]);
      return;
    }

    setUserVisibleColumns((prev) => prev.filter((key) => key !== groupKey && !subKeys.includes(key)));
  };

  const handleChildChange = (checkedKeys, groupKey) => {
    if (isRemovedJobDataKey(groupKey)) return;
    userTouchedRef.current = true;
    const otherKeys = sanitizeRemovedJobKeys(visibleColumns.filter((key) => key && !key.startsWith(`${groupKey}_`)));
    const parentKey = visibleColumns.includes(groupKey) ? [groupKey] : [];
    setUserVisibleColumns([...otherKeys, ...parentKey, ...checkedKeys]);
  };

  const GROUP_KEYS = ["glide", "phase", "adme", "molprops"];

  const handleSelectAllColumns = (e) => {
    userTouchedRef.current = true;
    if (e.target.checked) {
      const allStaticKeys = sanitizeRemovedJobKeys(ALL_COLUMNS.map((c) => c.key));
      const toSet = Array.from(new Set([...allStaticKeys, ...allPossibleDynamicKeys, ...GROUP_KEYS]));
      setUserVisibleColumns(toSet);
      return;
    }

    setUserVisibleColumns(ALWAYS_VISIBLE);
  };

  const selectableKeys = useMemo(
    () => new Set(sanitizeRemovedJobKeys([...ALL_COLUMNS.map((c) => c.key), ...allPossibleDynamicKeys])),
    [allPossibleDynamicKeys]
  );

  const allColumnsSelected = useMemo(() => {
    if (!selectableKeys.size) return false;
    const current = sanitizeRemovedJobKeys(visibleColumns);
    return Array.from(selectableKeys).every((key) => current.includes(key));
  }, [visibleColumns, selectableKeys]);

  const someColumnsSelected = !allColumnsSelected && sanitizeRemovedJobKeys(visibleColumns).length > ALWAYS_VISIBLE.length;

  return {
    visibleColumns,
    setVisibleColumns: (next) => {
      setUserVisibleColumns(next);
    },
    colsPopoverVis,
    setColsPopoverVis,
    expandedGroups,
    setExpandedGroups,
    allPossibleDynamicKeys,
    handleParentChange,
    handleChildChange,
    handleSelectAllColumns,
    allColumnsSelected,
    someColumnsSelected,
  };
};
